import { Button, Card, CardContent, Expand, Flex, Image, Text } from '@bitrise/bitkit';
import classnames from 'classnames';
import React, { useState } from 'react';

import { trackEvent } from '../../utils/tracking';
import TraceLogo from './LogoTrace.svg';

import './TraceInfoCard.css';

const sections: {
  [key: string]: {
    id: string;
    title: string;
    copy: string;
  }[];
} = {
  aboveFold: [
    {
      id: 'section-1',
      title: 'Detect the problem',
      copy: 'Know about issues before your users report them.',
    },
    {
      id: 'section-2',
      title: 'Assess the impact',
      copy: 'Focus on resolving the issues which are most impactful to your users.',
    },
    {
      id: 'section-3',
      title: 'Trace the cause',
      copy: 'Spend less time trying to reproduce bugs.',
    },
  ],
  belowFold: [
    {
      id: 'section-4',
      title: 'Stability',
      copy: 'How many errors and crashes are my app generating?',
    },
    {
      id: 'section-5',
      title: 'Responsiveness',
      copy: 'How long does my app take to start or transition between views?',
    },
    {
      id: 'section-6',
      title: 'Consumption',
      copy: 'How much CPU and Memory resource is your app consuming?',
    },
  ],
};

const TraceInfoCard: React.FunctionComponent = () => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const classes = classnames('TraceInfoCard', {
    'TraceInfoCard--Expanded': isCardExpanded,
  });

  const onExpandButtonClick = () => {
    setIsCardExpanded(!isCardExpanded);

    if (!isCardExpanded) {
      trackEvent('AppSelector.ExpandVP', {});
    }
  };

  return (
    <Flex className={classes}>
      <Card elevation="x2" container>
        <CardContent padding="x8">
          <Flex
            className="TraceInfoCard__Content"
            direction="vertical"
            gap="x10"
            minHeight="46.75rem"
          >
            {/* Logo */}
            <Flex direction="horizontal" gap="x6">
              <Image src={TraceLogo} width="4rem" height="4rem" />

              <Flex direction="vertical" shrink>
                <Text config="3" Component="h1">
                  Trace
                </Text>

                <Text config="7">Monitoring made for mobile.</Text>
              </Flex>
            </Flex>

            {/* Lead copy */}
            <Text config="7">
              Publish to the app stores with confidence - Trace will help you detect, identify, and
              resolve issues before users report them.
            </Text>

            <Flex direction="vertical" gap="x12">
              {/* Sections - Above fold */}
              <Flex direction="vertical" gap="x10">
                {sections.aboveFold.map(({ id, title, copy }) => (
                  <Flex key={id} direction="vertical" gap="x2">
                    <Text Component="h2" config="5">
                      {title}
                    </Text>
                    <Text config="7">{copy}</Text>
                  </Flex>
                ))}
              </Flex>

              <Expand expanded={isCardExpanded} peekHeight="11.25rem">
                {/* Sections - Below fold */}
                <Flex direction="vertical" gap="x10" paddingVertical="x12">
                  {sections.belowFold.map(({ id, title, copy }) => (
                    <Flex key={id} direction="vertical" gap="x2">
                      <Text Component="h2" config="5">
                        {title}
                      </Text>

                      <Text config="7">{copy}</Text>
                    </Flex>
                  ))}
                </Flex>
              </Expand>
            </Flex>
          </Flex>
        </CardContent>

        <Flex
          borderRadius="x2"
          className="TraceInfoCard__ExpandOverlay"
          direction="vertical"
          height="11.25rem"
          paddingVertical="x12"
          alignChildrenHorizontal="middle"
        >
          <Button
            type="button"
            onClick={onExpandButtonClick}
            level="secondary"
            size="small"
            elevation="x1"
          >
            Read {!isCardExpanded ? 'more' : 'less'}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
};

export default TraceInfoCard;
